"use client";

import * as stylex from "@stylexjs/stylex";
import { ArrowRight, ChevronRight, Globe, LocateFixed, LocateOff, type LucideIcon, MapPinOff, Search, SearchX } from "lucide-react";
import type { FormEvent } from "react";
import { copy } from "@/copy";
import type { WeatherLocation } from "@/domain/schemas";
import { timeZoneName } from "@/domain/zoneName";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import { Button } from "@/ui/Button";
import { Eyebrow } from "@/ui/Eyebrow";
import { Icon } from "@/ui/Icon";
import { press } from "@/ui/press";
import { TextInput } from "@/ui/TextInput";
import type { LocateFailure, WeatherSetupState } from "../weatherSetup";
const pulse = "xw0kf14-B";
const styles = {
  form: {
    kWkggS: "xh540kj",
    kaIpWk: "x1fv5t7x",
    kmVPX3: "x1qhigcl",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "xddi9bj",
    $$css: true
  },
  match: {
    kAzted: "x8nclml",
    kzqmXN: "xh8yej3",
    kMzoRj: "xc342km",
    kaIpWk: "x1fv5t7x",
    kWkggS: "x5r8ks9",
    k8WAf4: "x1f8xy1r",
    kg3NbH: "xw3ndq7",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1hu4l6k",
    kVVagm: "xln7xf2",
    kMwMTN: "x1heor9g",
    k9WMMc: "xdpxx8g",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "xgdialr",
    kAMwcw: "x9lcvmn",
    $$css: true
  }
};
type MatchesProps = {
  matches: readonly WeatherLocation[];
  onPick: (place: WeatherLocation) => void;
};
const NOTICE_ICONS: Record<LocateFailure, LucideIcon> = {
  denied: MapPinOff,
  "no-position": LocateOff,
  outside: Globe,
  lookup: SearchX
};
export type PlaceFormProps = {
  state: WeatherSetupState;
  canLocate: boolean;
  submitLabel: string;
  onType: (draft: string) => void;
  onSubmit: () => void;
  onPick: (place: WeatherLocation) => void;
  onLocate: () => void;
  xstyle?: stylex.StyleXStyles;
};
export function PlaceForm({
  state,
  canLocate,
  submitLabel,
  onType,
  onSubmit,
  onPick,
  onLocate,
  xstyle
}: PlaceFormProps) {
  const error = state.step === "editing" && state.error && copy.weather.setup.error[state.error];
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (state.step === "editing") onSubmit();
  };
  return <form noValidate onSubmit={submit} {...stylex.props(styles.form, xstyle)}>
      <TextInput label={copy.weather.setup.label} placeholder={copy.weather.setup.placeholder} value={state.draft} onChange={onType} error={error || undefined} />
      {state.step === "searching" && <Searching query={state.query} />}
      {state.step === "locating" && <Locating />}
      {state.step === "editing" && state.notice && <Notice notice={state.notice} />}
      {state.step === "choosing" && <Matches matches={state.matches} onPick={onPick} />}
      {state.step === "editing" && <div className="x78zum5 x1a02dak x6s0dn4 x1hu4l6k">
          <Button type="submit" iconRight={ArrowRight}>
            {submitLabel}
          </Button>
          {canLocate && <Button type="button" variant="secondary" icon={LocateFixed} onClick={onLocate}>
              {copy.weather.setup.locate}
            </Button>}
        </div>}
    </form>;
}
function Matches({
  matches,
  onPick
}: MatchesProps) {
  const city = matches[0]?.name ?? "";
  return <div className="x78zum5 xdt5ytf x152posx">
      <Eyebrow>{copy.weather.setup.matches(matches.length, city)}</Eyebrow>
      {matches.map(place => <button key={`${place.lat},${place.lon}`} type="button" onClick={() => onPick(place)} {...stylex.props(styles.match, press.scale)}>
          <span className="x78zum5 xdt5ytf x195vfkc xeuugli">
            <span className="xwsyq91 xfbjfs5 x1yrajzq">{place.name}</span>
            <span className="x1jvydc1 x15i68ds">
              {copy.weather.setup.region(place.region, place.timeZone && timeZoneName(place.timeZone))}
            </span>
          </span>
          <Icon icon={ChevronRight} size={22} />
        </button>)}
    </div>;
}
function Searching({
  query
}: {
  query: string;
}) {
  return <div role="status" className="x78zum5 x6s0dn4 x1hu4l6k x1gg8mnh x19d36u7 x15i68ds">
      <span className="x78zum5 x1okw0bk x19utubg x1m9vv7p x4hg4is xa4qsjk xpz12be">
        <Icon icon={Search} size={22} />
      </span>
      <span>{copy.weather.setup.searching(query)}</span>
    </div>;
}
function Locating() {
  return <div role="status" className="x78zum5 x6s0dn4 x1hu4l6k x1gg8mnh x19d36u7 x15i68ds">
      <span className="x78zum5 x1okw0bk x19utubg x1m9vv7p x4hg4is xa4qsjk xpz12be">
        <Icon icon={LocateFixed} size={22} />
      </span>
      <span className="x78zum5 xdt5ytf x195vfkc xeuugli">
        <span>{copy.weather.setup.locating}</span>
        <span className="x1jvydc1">{copy.weather.setup.locatingHint}</span>
      </span>
    </div>;
}
function Notice({
  notice
}: {
  notice: LocateFailure;
}) {
  return <div role="status" className="x78zum5 x1cy8zhl x1hu4l6k x5r8ks9 x1fv5t7x x1vvitgl x1j61zf2 x15i68ds xfrs9s4 x1fzhlzt">
      <span className="x78zum5 x1okw0bk">
        <Icon icon={NOTICE_ICONS[notice]} size={22} />
      </span>
      <span className="xeuugli">{copy.weather.setup.notice[notice]}</span>
    </div>;
}